import { en } from '../locales/en';
import type { CenterUnitSystem } from '../types/gcs/center';
import type { MissionId } from '../types/gcs/mission';
import type { VehicleId } from '../types/gcs/vehicle';

export interface UnitFormatters {
  formatWeight(weightKg: number): string;
  formatSpeed(speedMs: number): string;
  formatDistance(distanceM: number): string;
  formatAltitude(altitudeM: number): string;
}

const KG_TO_LB = 2.2046226218;
const MS_TO_KT = 1.9438444924;
const M_TO_NMI = 0.0005399568;
const M_TO_FT = 3.280839895;
const METRIC_DISTANCE_THRESHOLD_M = 1000;
const IMPERIAL_DISTANCE_THRESHOLD_NMI = 0.1;

const metricUnitFormatters: UnitFormatters = {
  formatWeight(weightKg: number): string {
    const rounded = Math.round(weightKg * 100) / 100;
    return en.units.weightKg.replace('{value}', String(rounded));
  },

  formatSpeed(speedMs: number): string {
    const rounded = Math.round(speedMs);
    return en.units.speedMs.replace('{value}', String(rounded));
  },

  formatDistance(distanceM: number): string {
    if (distanceM < METRIC_DISTANCE_THRESHOLD_M) {
      const roundedMeters = Math.round(distanceM);
      return en.units.distanceM.replace('{value}', String(roundedMeters));
    }

    const km = distanceM / 1000;
    const rounded = Math.round(km * 10) / 10;
    return en.units.distanceKm.replace('{value}', String(rounded));
  },

  formatAltitude(altitudeM: number): string {
    const rounded = Math.round(altitudeM);
    return en.units.altitudeM.replace('{value}', String(rounded));
  }
};

const imperialUnitFormatters: UnitFormatters = {
  formatWeight(weightKg: number): string {
    const lb = weightKg * KG_TO_LB;
    const rounded = Math.round(lb * 100) / 100;
    return en.units.weightLb.replace('{value}', String(rounded));
  },

  formatSpeed(speedMs: number): string {
    const knots = speedMs * MS_TO_KT;
    const rounded = Math.round(knots);
    return en.units.speedMph.replace('{value}', String(rounded));
  },

  formatDistance(meters: number): string {
    const nauticalMiles = meters * M_TO_NMI;

    if (nauticalMiles < IMPERIAL_DISTANCE_THRESHOLD_NMI) {
      const feet = meters * M_TO_FT;
      const roundedFeet = Math.round(feet);
      return en.units.distanceFt.replace('{value}', String(roundedFeet));
    }

    const rounded = Math.round(nauticalMiles * 10) / 10;
    return en.units.distanceNM.replace('{value}', String(rounded));
  },

  formatAltitude(altitudeM: number): string {
    const feet = altitudeM * M_TO_FT;
    const rounded = Math.round(feet);
    return en.units.altitudeFt.replace('{value}', String(rounded));
  }
};

export function getUnitFormatters(unitSystem: CenterUnitSystem): UnitFormatters {
  return unitSystem === 'imperial' ? imperialUnitFormatters : metricUnitFormatters;
}

/**
 * Formats a percentage value.
 * @param pct - The percentage value
 * @returns Formatted percentage string (e.g., "75%")
 */
export function formatPercentage(pct: number): string {
  const rounded = Math.round(pct);
  return en.units.percentage.replace('{value}', String(rounded));
}

/**
 * Extracts the first 4 characters from a vehicle id, if present.
 * @param vehicleId - The full vehicle id
 * @returns Short vehicle id
 */
export function getShortVehicleId(vehicleId: VehicleId): string {
  return String(vehicleId).slice(0, 4);
}

/**
 * Extracts the first 6 characters from a mission id, if present.
 * @param missionId - The full mission id
 * @returns Short mission id or null when no characters exist
 */
export function getShortMissionId(missionId: MissionId): string {
  return missionId.slice(0, 6);
}

/**
 * Formats latitude and longitude coordinates with fixed precision.
 * @param latitude - Latitude value
 * @param longitude - Longitude value
 * @param precision - Number of decimal places to display
 * @returns Formatted coordinates string (e.g., "32.123456, -97.654321")
 */
export function formatCoordinates(latitude: number, longitude: number, precision = 6): string {
  return `${latitude.toFixed(precision)}, ${longitude.toFixed(precision)}`;
}

/**
 * Formats milliseconds into a countdown time string.
 * @param ms - The time in milliseconds
 * @returns Formatted time string (e.g., "1:23" or "0:45")
 */
export function formatTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
